import { type FC } from "react";
import Grid from "@mui/material/Grid";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import copy from "copy-to-clipboard";
import type { Stream, StreamContent } from "@/partner/entities/streams";
import {
  useStartStreamMutation,
  useFinishStreamMutation,
  useReopenStreamMutation,
  useResetStreamKeyMutation,
  useCheckStreamConnectionMutation,
  useStartStreamPullMutation,
  useStopStreamPullMutation,
} from "@/partner/entities/streams";
import {
  useStreamEntrypointsListQuery,
  useAttachStreamEntrypointMutation,
  type StreamEntrypointsItem,
} from "@/partner/entities/streamEntrypoints";
import { useConfirmAction } from "@/shared/components";
import { PullBlock } from "@/partner/shared/components/PullBlock";
import { StreamStatusType } from "@/types/graphql/partner.generated";
import { StreamDialogHeader } from "./StreamDialogHeader";
import { StreamPlayerPreview } from "./StreamPlayerPreview";
import { ShareIcons } from "./ShareIcons";

// Как DEFAULT в референсном StreamEntrypointField.js — псевдо-id пункта
// «Собственный вход» в селекте, отличающийся от реальных id точек.
const DEFAULT_ENTRYPOINT = "DEFAULT";

// Совпадает с SPACING референсного VideoForm.tsx — та же компоновка формы,
// просто с полями стрима.
const SPACING = 1;

// Тот же приём, что и в StreamsListItem.tsx: полный Record по
// сгенерированному enum'у (а не строковым литералам "PENDING"/...), чтобы
// новый статус не провалился молча и не разошёлся с реальным типом Stream.status.
const STREAM_STATUS_CHIP_COLOR: Record<
  StreamStatusType,
  "default" | "success"
> = {
  [StreamStatusType.PendingType]: "default",
  [StreamStatusType.StartedType]: "success",
  [StreamStatusType.FinishedType]: "default",
  [StreamStatusType.CancelledType]: "default",
};

// Как и STREAM_STATUS_CHIP_COLOR выше: статический Record вместо
// t(`streams.status.${status}`) — динамический ключ не проходит
// i18next-извлечение и не виден статическому аудиту переводов.
const STREAM_STATUS_LABEL_KEYS: Record<StreamStatusType, string> = {
  [StreamStatusType.PendingType]: "streams.status.PENDING",
  [StreamStatusType.StartedType]: "streams.status.STARTED",
  [StreamStatusType.FinishedType]: "streams.status.FINISHED",
  [StreamStatusType.CancelledType]: "streams.status.CANCELLED",
};

interface StreamBroadcastTabProps {
  content: StreamContent;
  stream: Stream;
  onClose: () => void;
}

// Временный перенос broadcast-контента StreamForm.tsx как есть (Task 4);
// раскладка меняется в Task 7.
export const StreamBroadcastTab: FC<StreamBroadcastTabProps> = ({
  content,
  stream,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  // Наш диалог вместо window.confirm: нативное окно не умеет показывать ход
  // запроса и гасит ошибку в снекбаре, который успевает исчезнуть.
  const { confirm, dialog: confirmDialog } = useConfirmAction();

  const [startStream, { loading: starting }] = useStartStreamMutation({
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });
  const [finishStream, { loading: finishing }] = useFinishStreamMutation({
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });
  // Без onError: промис мутации должен отклониться, чтобы диалог показал
  // ошибку у себя и дал повторить, не открывая вкладку заново.
  const [reopenStream, { loading: reopening }] = useReopenStreamMutation({
    onCompleted: () =>
      enqueueSnackbar(t("streams.form.reopenSuccess"), { variant: "success" }),
  });
  const [resetStreamKey, { loading: resetting }] = useResetStreamKeyMutation({
    onCompleted: () =>
      enqueueSnackbar(t("streams.form.keyResetSuccess"), {
        variant: "success",
      }),
  });
  const [checkConnection, { loading: checking }] =
    useCheckStreamConnectionMutation({
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });
  const [startStreamPull, { loading: startingPull }] =
    useStartStreamPullMutation({
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });
  const [stopStreamPull, { loading: stoppingPull }] = useStopStreamPullMutation(
    {
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    }
  );

  // Без пагинации/поиска — та же схема, что у StreamEntrypointAttachField
  // (первая страница вариантов селекта хватает для реалистичного числа
  // точек входа платформы).
  const { data: entrypointsData } = useStreamEntrypointsListQuery({
    variables: { page: 0, rowsPerPage: 100 },
  });
  const entrypointOptions: StreamEntrypointsItem[] =
    entrypointsData?.streamEntrypoints.nodes ?? [];

  // Рефетч Stream — тот же приём, что у соседних мутаций вкладки
  // (UpdateStreamMutation в StreamEditTab, деструкции рестрима в
  // StreamRestreamsTab): значение селекта выводится прямо из
  // stream.entrypoint, без локального состояния, поэтому после успешного
  // рефетча оно само встаёт в новое положение; при ошибке — остаётся на
  // месте (мутация не долетела до кэша).
  const [attachStreamEntrypoint, { loading: attaching }] =
    useAttachStreamEntrypointMutation({
      refetchQueries: ["Stream"],
      onCompleted: () =>
        enqueueSnackbar(t("streamEntrypoints.attach.success"), {
          variant: "success",
        }),
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });

  const handleEntrypointChange = (nextValue: string) => {
    if (nextValue === DEFAULT_ENTRYPOINT) {
      if (!stream.entrypoint) return;
      void attachStreamEntrypoint({
        variables: { id: stream.entrypoint.id, contentId: null },
      });
    } else {
      void attachStreamEntrypoint({
        variables: { id: nextValue, contentId: content.id },
      });
    }
  };

  const handleCopy = (value: string) => {
    copy(value);
    enqueueSnackbar(t("streams.form.copied"), { variant: "success" });
  };

  const handleReopen = () => {
    confirm({
      text: t("streams.form.confirmReopen"),
      onConfirm: () => reopenStream({ variables: { contentId: content.id } }),
    });
  };

  const handleResetKey = () => {
    confirm({
      text: t("streams.form.confirmResetKey"),
      onConfirm: () => resetStreamKey({ variables: { contentId: content.id } }),
    });
  };

  // Референсный StreamForm.js:328 — «опубликован» значит publishAt уже
  // наступил, а не просто выставлен (черновик с publishAt в будущем — ещё
  // не опубликован).
  const isPublished =
    Boolean(content.publishAt) && new Date(content.publishAt) <= new Date();

  return (
    <>
      <StreamDialogHeader
        title={t("streams.form.dialogTitle")}
        onClose={onClose}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Второстепенные элементы (счётчик зрителей, чип подключения,
              refresh) скрываются на xs — иначе шапка переполняется на
              узких экранах (~360-400px). Статус-чип и кнопка Start/Finish
              остаются видимыми всегда. */}
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            {t("streams.form.viewersOnline", {
              count: content.viewersOnline,
            })}
          </Typography>
          <Chip
            size="small"
            color={STREAM_STATUS_CHIP_COLOR[stream.status]}
            label={t(STREAM_STATUS_LABEL_KEYS[stream.status])}
          />
          <Chip
            size="small"
            variant="outlined"
            color={stream.liveConnected ? "success" : "default"}
            label={t(
              stream.liveConnected
                ? "streams.form.connected"
                : "streams.form.notConnected"
            )}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          />
          <Tooltip title={t("streams.form.checkConnection")}>
            <span>
              <IconButton
                size="small"
                disabled={checking}
                onClick={() =>
                  checkConnection({ variables: { contentId: content.id } })
                }
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {stream.status === StreamStatusType.StartedType ? (
            <Button
              variant="outlined"
              color="error"
              disabled={finishing}
              onClick={() =>
                finishStream({ variables: { contentId: content.id } })
              }
            >
              {t("streams.form.finishButton")}
            </Button>
          ) : stream.status === StreamStatusType.CancelledType ? null : (
            // Отменённый эфир (отмена события) не стартует и не
            // переоткрывается: бэкенд такой переход отклоняет.
            <>
              {/* Завершённый эфир возвращается в ожидание: после тестового
                  прогона зрители не должны видеть «эфир закончился». */}
              {stream.status === StreamStatusType.FinishedType && (
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={reopening}
                  onClick={handleReopen}
                >
                  {t("streams.form.reopenButton")}
                </Button>
              )}
              {/* Стартовать можно и из FINISHED, пока сигнал живой: обрыв
                  связи посреди эфира не должен требовать смены ключа. */}
              <Button
                variant="contained"
                color="success"
                disabled={!stream.liveConnected || starting}
                onClick={() =>
                  startStream({ variables: { contentId: content.id } })
                }
              >
                {stream.status === StreamStatusType.FinishedType
                  ? t("streams.form.resumeButton")
                  : t("streams.form.startButton")}
              </Button>
            </>
          )}
        </Stack>
      </StreamDialogHeader>

      <Grid container spacing={SPACING}>
        <Grid size={{ xs: 12, sm: 6, md: 7 }}>
          <Grid container spacing={SPACING}>
            <Grid size={12}>
              <Card elevation={0}>
                <StreamPlayerPreview
                  stream={stream}
                  posterUrl={content.preview?.url}
                />
              </Card>
            </Grid>
            <Grid size={12}>
              <Card elevation={0}>
                <CardContent>
                  <Stack spacing={2}>
                    <ShareIcons
                      shareUrl={String(content.url ?? "")}
                      shareText={content.title}
                      disabledText={
                        isPublished
                          ? null
                          : t("streams.form.share.publishToShare")
                      }
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 5 }}>
          <Card elevation={0}>
            <CardHeader title={t("streams.form.broadcastCardTitle")} />
            <CardContent>
              {stream.lastError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {stream.lastError.code}: {stream.lastError.message}
                </Alert>
              )}

              <Typography gutterBottom>
                {t("streams.form.broadcastHelp.p1")}
              </Typography>
              <Typography gutterBottom>
                {t("streams.form.broadcastHelp.p2")}
              </Typography>

              <Grid container spacing={4}>
                {(entrypointOptions.length > 0 || stream.entrypoint) && (
                  <Grid size={12}>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label={t("streams.form.entrypointField")}
                      value={stream.entrypoint?.id ?? DEFAULT_ENTRYPOINT}
                      disabled={attaching}
                      onChange={(event) =>
                        handleEntrypointChange(event.target.value)
                      }
                    >
                      <MenuItem value={DEFAULT_ENTRYPOINT}>
                        {t("streams.form.entrypointDefault")}
                      </MenuItem>
                      {entrypointOptions.map((entrypoint) => (
                        <MenuItem key={entrypoint.id} value={entrypoint.id}>
                          {entrypoint.title}
                        </MenuItem>
                      ))}
                    </TextField>
                    {stream.entrypoint && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {t("streams.form.entrypointHint", {
                          title: stream.entrypoint.title,
                        })}
                      </Alert>
                    )}
                  </Grid>
                )}
                <Grid size={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="RTMPS URL"
                    value={stream.rtmpsUrl}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Tooltip title={t("streams.form.copyTooltip")}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(stream.rtmpsUrl)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("streams.form.streamKeyField")}
                    value={stream.rtmpsStreamKey}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Tooltip title={t("streams.form.copyTooltip")}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(stream.rtmpsStreamKey)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="SRT URL"
                    value={stream.srtUrl}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Tooltip title={t("streams.form.copyTooltip")}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(stream.srtUrl)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("streams.form.srtStreamIdField")}
                    value={stream.srtStreamId}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Tooltip title={t("streams.form.copyTooltip")}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(stream.srtStreamId)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t("streams.form.srtPassphraseField")}
                    value={stream.srtPassphrase}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Tooltip title={t("streams.form.copyTooltip")}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(stream.srtPassphrase)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="text"
                  disabled={
                    stream.status === StreamStatusType.StartedType || resetting
                  }
                  onClick={handleResetKey}
                >
                  {t("streams.form.resetKeyButton")}
                </Button>
              </Stack>

              <Box sx={{ mt: 3 }}>
                <PullBlock
                  pull={stream.pull}
                  liveConnected={stream.liveConnected}
                  starting={startingPull}
                  stopping={stoppingPull}
                  onStart={(sourceUrl) =>
                    void startStreamPull({
                      variables: { contentId: content.id, sourceUrl },
                    })
                  }
                  onStop={() =>
                    void stopStreamPull({
                      variables: { contentId: content.id },
                    })
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {confirmDialog}
    </>
  );
};
